import {NumberString, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ProductCostsV2, {ProductCostsV2Fields} from '../../../models/productCostsV2Model';
import {ISO_DATE_FORMAT} from '../../../util/constants';
import {LVBSize} from '../../../util/enums';
import {ActivateHandle, EditMode} from '../../../util/interfaces';
import BolProduct from '../../../models/bolProductModel';
import {
    buildPeriodEndDates,
    createProductCostsForm,
    findDuplicatePeriod,
    sectionsWithErrors,
    validateProductCostsForm
} from '../../../util/productCostsFormUtil';
import {getProductDimensions} from '../../../util/requestSender';
import {getLVBSizeFromDimensions} from '../../../util/salesDashboardUtil';
import {ProductCostsFormErrors, ProductCostsSection, ProductCostsValidation} from '../../../util/types';
import {createErrorMessage} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import EditCostsDialog from './editCostsDialog';

interface Props {
    onSuccess: (ean: string) => void;
}

function EditCostsDialogController(props: Props, ref: ForwardedRef<ActivateHandle<BolProduct>>) {
    const {onSuccess} = props;
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [product, setProduct] = useState<BolProduct | null>(null);
    const [records, setRecords] = useState<Array<ProductCostsV2>>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [productCosts, setProductCosts] = useState<ProductCostsV2>(createProductCostsForm(null, ids(product)));
    const [lvbSize, setLvbSize] = useState<LVBSize | null>(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [openSections, setOpenSections] = useState<Set<ProductCostsSection>>(new Set());
    const [errors, setErrors] = useState<ProductCostsFormErrors>({});
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [dataMode, setDataMode] = useState(EditMode.NEW);
    const [periodValue, setPeriodValue] = useState<'forever' | 'today' | 'fromDate'>('forever');
    const periodEnds = useMemo(() => buildPeriodEndDates(records), [records]);
    const effectiveLvbSize = lvbSize ?? productCosts?.lvbSize ?? null;
    const activeEan = useRef<string | null>(null);

    function onActivate(activated?: BolProduct) {
        if (!activated || !account) return;
        activeEan.current = activated.ean;
        setProduct(activated);
        setRecords([]);
        setProductCosts(createProductCostsForm(null, ids(activated)));
        setDataMode(EditMode.NEW);
        setLvbSize(null);
        setAdvancedOpen(false);
        setOpenSections(new Set());
        setErrors({});
        setSaving(false);
        setDeletingId(null);
        setActive(true);
        loadRecords(activated, true);
        loadLvbSize(activated);
        setPeriodValue('forever');
    }

    function onClose() {
        if (saving || deletingId) return;
        activeEan.current = null;
        setActive(false);
    }

    function onChangePeriodValue(period: 'forever' | 'today' | 'fromDate') {
        setPeriodValue(period);
        let dateValue: IDate;
        if (period === 'forever') dateValue = new IDate('2020-01-01');
        else dateValue = new IDate();
        setProductCosts((current: ProductCostsV2) => current.clone().setFlex('forever_selected', period === 'forever'));
        onChangeStartDate(dateValue);
    }

    function onChangeForm(key: keyof ProductCostsV2Fields, numeric: boolean = true) {
        return function (value: string | NumberString) {
            const fieldValue = numeric ? new NumberString(value).nullNumberValue : value;
            setProductCosts((current: ProductCostsV2) => current.clone({[key]: fieldValue}));
            resetError(key);
        };
    }

    function onChangeStartDate(value: IDate) {
        setProductCosts((current: ProductCostsV2) => current.clone({start_date: value.format(ISO_DATE_FORMAT)}));
    }

    function onToggleAdvanced(open: boolean) {
        setAdvancedOpen(open);
    }

    function onToggleSection(section: ProductCostsSection) {
        return function (open: boolean) {
            setOpenSections(current => {
                const next = new Set(current);
                if (open) next.add(section);
                else next.delete(section);
                return next;
            });
        };
    }

    function onEditCosts(costs: ProductCostsV2) {
        return function () {
            if (!product || !account || deletingId || saving) return;
            setProductCosts(createProductCostsForm(costs, ids(product)));
            setErrors({});
            setDataMode(EditMode.EDIT);
            if (costs.getFlex('forever_selected') === true) setPeriodValue('forever');
            else if (IDate.now().isAt(costs.startDateAsDate, 'day')) setPeriodValue('today');
            else setPeriodValue('fromDate');
        };
    }

    function onAddRecord() {
        startNewRecord();
    }

    function onStopEditing() {
        startNewRecord();
    }

    function onDeleteRecord(record: ProductCostsV2) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            if (!record.id.length || deletingId || saving) return;
            showDialog(
                t('products.editCosts.confirmDelete', 'Weet je zeker dat je deze productkosten wilt verwijderen?'),
                t('products.editCosts.delete', 'Verwijderen'),
                [
                    t('products.editCosts.no', 'Nee'),
                    {label: t('products.editCosts.yes', 'Ja'), primary: false, onClick: deleteRecord(record)}
                ]
            );
        };
    }

    function onSubmit() {
        if (!product || saving) return;
        const validation = validateProductCostsForm(productCosts);
        if (Object.keys(validation).length) {
            setErrors(translateErrors(validation));
            const failing = sectionsWithErrors(validation);
            if (failing.length) {
                setAdvancedOpen(true);
                setOpenSections(current => new Set([...current, ...failing]));
            }
            return;
        }
        if (findDuplicatePeriod(records, productCosts)) {
            showDialog(
                t('products.editCosts.duplicateDate', 'Er bestaan al kosten met deze ingangsdatum en dit aantal stuks.')
            );
            return;
        }
        setSaving(true);
        const costs = productCosts.clone({lvb_size: effectiveLvbSize});
        if (dataMode === EditMode.NEW) onCreate(costs);
        else onUpdate(costs);
    }

    function onCreate(costs: ProductCostsV2) {
        costs.create((_: ProductCostsV2, success: boolean, data: ResponseData) => {
            setSaving(false);
            if (success) {
                showSnackbar(t('products.editCosts.created', 'De productkosten zijn opgeslagen'));
                onSuccess(product!.ean);
                activeEan.current = null;
                setActive(false);
            } else onRequestFailed(data.message);
        }, onRequestFailed);
    }

    function onUpdate(costs: ProductCostsV2) {
        costs.update((_: ProductCostsV2, success: boolean, data: ResponseData) => {
            setSaving(false);
            if (success) {
                showSnackbar(t('products.editCosts.updated', 'De productkosten zijn bijgewerkt'));
                onSuccess(product!.ean);
                activeEan.current = null;
                setActive(false);
            } else onRequestFailed(data.message);
        }, onRequestFailed);
    }

    function onRequestFailed(message?: string | Response) {
        setSaving(false);
        const errMessage = typeof message === 'string' ? message : undefined;
        showDialog(
            createErrorMessage(
                errMessage ?? t('products.editCosts.saveFailed', 'Het opslaan van de productkosten is niet gelukt.')
            ),
            t('products.editCosts.saveFailedTitle', 'Opslaan mislukt')
        );
    }

    function startNewRecord() {
        if (!product || !account || deletingId || saving) return;
        setProductCosts(createProductCostsForm(null, ids(product)));
        setDataMode(EditMode.NEW);
        setErrors({});
        setPeriodValue('forever');
    }

    function deleteRecord(record: ProductCostsV2) {
        return function () {
            if (!product) return;
            setDeletingId(record.id);
            record.delete((_: ProductCostsV2, success: boolean, data: ResponseData) => {
                setDeletingId(null);
                if (success) {
                    showSnackbar(t('products.editCosts.deleted', 'De productkosten zijn verwijderd'));
                    onSuccess(product.ean);
                    loadRecords(product, false);
                    setPeriodValue('forever');
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'products.editCosts.deleteFailed',
                                    'Het verwijderen van de productkosten is niet gelukt.'
                                )
                        ),
                        t('products.editCosts.deleteFailedTitle', 'Verwijderen mislukt')
                    );
            }, onDeleteFailed);
        };
    }

    function onDeleteFailed() {
        setDeletingId(null);
        showDialog(
            createErrorMessage(
                t('products.editCosts.deleteFailed', 'Het verwijderen van de productkosten is niet gelukt.')
            ),
            t('products.editCosts.deleteFailedTitle', 'Verwijderen mislukt')
        );
    }

    function loadRecords(product: BolProduct, initial: boolean) {
        if (!account) return;
        setLoadingRecords(true);
        ProductCostsV2.getAll(
            [account.id],
            product.ean,
            (loaded: Array<ProductCostsV2>, success: boolean) => {
                if (activeEan.current !== product.ean) {
                    if (activeEan.current === null) setLoadingRecords(false);
                    return;
                }
                setLoadingRecords(false);
                if (!success) {
                    if (initial) setActive(false);
                    showDialog(
                        createErrorMessage(
                            t('products.editCosts.loadFailed', 'Het laden van de productkosten is niet gelukt.')
                        ),
                        t('products.editCosts.loadFailedTitle', 'Laden mislukt')
                    );
                    return;
                }
                setRecords(loaded);
                // Nothing is selected up front: the form starts as a blank new
                // period and only a click in the history loads a record into it.
                setErrors({});
                setProductCosts(createProductCostsForm(null, ids(product)));
            },
            () => onLoadRecordsFailed(product, initial)
        );
    }

    function onLoadRecordsFailed(product: BolProduct, initial: boolean) {
        if (activeEan.current !== product.ean) {
            if (activeEan.current === null) setLoadingRecords(false);
            return;
        }
        setLoadingRecords(false);
        if (initial) setActive(false);
        showDialog(
            createErrorMessage(t('products.editCosts.loadFailed', 'Het laden van de productkosten is niet gelukt.')),
            t('products.editCosts.loadFailedTitle', 'Laden mislukt')
        );
    }

    function loadLvbSize(product: BolProduct) {
        getProductDimensions({
            account_id: product.accountId,
            ean: product.ean,
            callback: (data: ResponseData) => {
                if (!data.success || activeEan.current !== product.ean) return;
                setLvbSize(getLVBSizeFromDimensions(data.dimensions));
            }
        });
    }

    function ids(product: BolProduct | null) {
        if (!product) return null;
        return {accountId: product.accountId, ean: product.ean};
    }

    function resetError(key: keyof ProductCostsV2Fields) {
        if (errors[key]) setErrors(current => ({...current, [key]: undefined}));
    }

    function translateErrors(validation: ProductCostsValidation): ProductCostsFormErrors {
        const translated: ProductCostsFormErrors = {};
        for (const key of Object.keys(validation) as Array<keyof ProductCostsV2Fields>)
            translated[key] =
                validation[key] === 'invalid'
                    ? t('products.editCosts.invalidAmount', 'Vul een geldig bedrag in')
                    : t('products.editCosts.negative', 'Het bedrag kan niet negatief zijn');
        return translated;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <EditCostsDialog
            onClose={onClose}
            onChangeForm={onChangeForm}
            onChangeStartDate={onChangeStartDate}
            onToggleAdvanced={onToggleAdvanced}
            onToggleSection={onToggleSection}
            onSelectRecord={onEditCosts}
            onAddRecord={onAddRecord}
            onStopEditing={onStopEditing}
            onDeleteRecord={onDeleteRecord}
            onChangePeriodValue={onChangePeriodValue}
            onSubmit={onSubmit}
            active={active}
            product={product}
            records={records}
            periodEnds={periodEnds}
            loadingRecords={loadingRecords}
            form={productCosts}
            lvbSize={effectiveLvbSize}
            advancedOpen={advancedOpen}
            openSections={openSections}
            errors={errors}
            saving={saving}
            deletingId={deletingId}
            dataMode={dataMode}
            periodValue={periodValue}
        />
    );
}

export default forwardRef<ActivateHandle<BolProduct>, Props>(EditCostsDialogController);
